import { useState, useEffect, createContext, useContext, ReactNode, useCallback, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import type { UserTier } from '@/config/tiers';
import { resolveInitialTier } from '@/lib/initialTier';
import { clearUserData, storeAuthToken, clearAuthToken, storeUserTier, getUserTier } from '@/lib/offline/db';
import { syncVocabularyFromServer } from '@/lib/offline/syncManager';
import { classifyAuthError, reconnectDelay } from '@/lib/authErrorClassifier';
import { useQueryClient } from '@tanstack/react-query';
import { clearPrivateQueries } from '@/lib/private-query-cache';

type PaymentStatus = 'ok' | 'past_due' | 'incomplete' | null;
type SubscriptionSource = 'stripe' | 'manual' | 'admin' | 'revenuecat' | null;

// --- Tier cache helpers (DF-3) ---
const TIER_CACHE_KEY = 'user_tier_cache';
const TIER_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

interface TierCacheEntry {
  tier: string;
  subscriptionEnd: string | null;
  paymentStatus: PaymentStatus;
  subscriptionSource: SubscriptionSource;
  cancelAtPeriodEnd?: boolean;
  timestamp: number;
}

function getCachedTier(): TierCacheEntry | null {
  try {
    const raw = localStorage.getItem(TIER_CACHE_KEY);
    if (!raw) return null;
    const parsed: TierCacheEntry = JSON.parse(raw);
    if (Date.now() - parsed.timestamp > TIER_CACHE_TTL) return null;
    return parsed;
  } catch {
    return null;
  }
}

function setCachedTier(entry: Omit<TierCacheEntry, 'timestamp'>) {
  try {
    localStorage.setItem(TIER_CACHE_KEY, JSON.stringify({ ...entry, timestamp: Date.now() }));
  } catch { /* quota exceeded – ignore */ }
}

function invalidateTierCache() {
  try { localStorage.removeItem(TIER_CACHE_KEY); } catch { /* ignore */ }
}

const SUPABASE_PROJECT_ID = 'mwagobcotsnleuttbvau';
const AUTH_STORAGE_KEY = `sb-${SUPABASE_PROJECT_ID}-auth-token`;
const SESSION_TIMEOUT_MS = 5000; // 5 second timeout for session fetch

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isOffline: boolean;
  tier: UserTier;
  tierInitialized: boolean; // Whether tier has been loaded at least once
  subscriptionEnd: string | null;
  subscriptionLoading: boolean;
  paymentStatus: PaymentStatus;
  subscriptionSource: SubscriptionSource;
  /** RFC 162: shared from the single check-subscription response */
  cancelAtPeriodEnd: boolean;
  checkSubscription: (forceRefresh?: boolean) => Promise<void>;
  signUp: (email: string, password: string, username: string, defaultLanguage?: string, intent?: string, nativeLanguage?: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  /** RFC-158: 'degraded' when the server is unreachable but the user stays signed in */
  connectionState: ConnectionState;
  retryConnection: () => Promise<void>;
}

export type ConnectionState = 'healthy' | 'degraded' | 'reconnecting';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Get cached session from localStorage for offline use
 */
function getCachedSession(): { user: User; session: Session } | null {
  try {
    const stored = localStorage.getItem(AUTH_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Supabase stores session data with user inside
      if (parsed?.user && parsed?.access_token) {
        return {
          user: parsed.user as User,
          session: {
            access_token: parsed.access_token,
            refresh_token: parsed.refresh_token,
            expires_in: parsed.expires_in,
            expires_at: parsed.expires_at,
            token_type: parsed.token_type || 'bearer',
            user: parsed.user,
          } as Session,
        };
      }
    }
  } catch (e) {
    console.warn('[Auth] Failed to parse cached session:', e);
  }
  return null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const previousQueryUser = useRef<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  useEffect(() => {
    if (previousQueryUser.current && previousQueryUser.current !== (user?.id ?? null)) {
      clearPrivateQueries(queryClient);
    }
    previousQueryUser.current = user?.id ?? null;
  }, [user?.id, queryClient]);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [tier, setTier] = useState<UserTier>('free');
  const [tierInitialized, setTierInitialized] = useState(false); // Track if tier has been set at least once
  const [subscriptionEnd, setSubscriptionEnd] = useState<string | null>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>(null);
  const [subscriptionSource, setSubscriptionSource] = useState<SubscriptionSource>(null);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(false);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const tokenRefreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const initializingRef = useRef(false);
  const subscriptionCheckInProgressRef = useRef(false);
  const lastSubscriptionCheckUserIdRef = useRef<string | null>(null);
  // Refs to avoid stale closures in callbacks
  const userRef = useRef<User | null>(null);
  const sessionRef = useRef<Session | null>(null);
  const isOfflineRef = useRef(isOffline);
  // RFC-158: server-connection state, separate from device offline state
  const [connectionState, setConnectionState] = useState<ConnectionState>('healthy');
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Keep refs in sync with state
  userRef.current = user;
  sessionRef.current = session;
  isOfflineRef.current = isOffline;

  const clearReconnectTimer = () => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  };

  const markHealthy = useCallback(() => {
    clearReconnectTimer();
    reconnectAttemptRef.current = 0;
    setConnectionState('healthy');
  }, []);

  const clearSignedOutState = () => {
    markHealthy();
    setSession(null);
    setUser(null);
    setTier('free');
    setTierInitialized(true);
    setSubscriptionEnd(null);
    setPaymentStatus(null);
    setSubscriptionSource(null);
    setCancelAtPeriodEnd(false);
    setLoading(false);
  };

  /** Refresh the session and update connection state. Never signs the user out on transient errors. */
  const refreshAndTrack = useCallback(async (label: string): Promise<void> => {
    try {
      const { data, error } = await supabase.auth.refreshSession();
      if (!error && data.session) {
        markHealthy();
        return;
      }
      if (classifyAuthError(error) === 'transient') {
        console.warn(`[Auth] ${label} refresh hit a server problem:`, error?.message);
        markDegraded();
      } else {
        // Fatal: supabase-js fires SIGNED_OUT, handled in onAuthStateChange
        console.warn(`[Auth] ${label} refresh failed:`, error?.message);
      }
    } catch (e) {
      console.warn(`[Auth] ${label} refresh error:`, e);
      markDegraded();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function markDegraded() {
    setConnectionState('degraded');
    if (reconnectTimerRef.current) return;
    const delay = reconnectDelay(reconnectAttemptRef.current);
    reconnectAttemptRef.current += 1;
    reconnectTimerRef.current = setTimeout(() => {
      reconnectTimerRef.current = null;
      if (userRef.current) void refreshAndTrack('Reconnect');
    }, delay);
  }

  const retryConnection = useCallback(async () => {
    clearReconnectTimer();
    setConnectionState('reconnecting');
    await refreshAndTrack('Manual retry');
  }, [refreshAndTrack]);

  useEffect(() => () => clearReconnectTimer(), []);

  // Track online/offline status
  useEffect(() => {
    const handleOnline = () => {
      console.log('[Auth] Network online');
      setIsOffline(false);
    };
    const handleOffline = () => {
      console.log('[Auth] Network offline');
      setIsOffline(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Proactive token refresh: refresh the session every 4 minutes
  // This keeps the access token fresh well before the default 1hr expiry,
  // preventing "Refresh Token Not Found" errors from stale tokens
  useEffect(() => {
    if (!user?.id || isOffline) {
      if (tokenRefreshIntervalRef.current) {
        clearInterval(tokenRefreshIntervalRef.current);
        tokenRefreshIntervalRef.current = null;
      }
      return;
    }

    const REFRESH_INTERVAL = 4 * 60 * 1000; // 4 minutes

    const refreshToken = async () => {
      await refreshAndTrack('Proactive');
    };

    tokenRefreshIntervalRef.current = setInterval(refreshToken, REFRESH_INTERVAL);

    return () => {
      if (tokenRefreshIntervalRef.current) {
        clearInterval(tokenRefreshIntervalRef.current);
        tokenRefreshIntervalRef.current = null;
      }
    };
  }, [user?.id, isOffline]);

  // Visibility-based refresh: when the app/tab regains focus, refresh the session
  // This handles mobile app backgrounding and tab throttling
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && userRef.current && !isOfflineRef.current) {
        await refreshAndTrack('Focus');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Ensure profile exists for authenticated user (called after email confirmation)
  const ensureProfileExists = async (userId: string, metadata: Record<string, unknown>) => {
    try {
      // Check if profile already exists
      const { data: existingProfile } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', userId)
        .single();

      if (!existingProfile) {
        // Create profile from user metadata
        const username = (metadata?.username as string) || `user_${userId.slice(0, 8)}`;
        const defaultLanguage = (metadata?.default_language as string) || 'spanish';
        const nativeLanguage = (metadata?.native_language as string) || 'english';

        const { error: profileError } = await supabase
          .from('profiles')
          .insert({
            user_id: userId,
            username,
            default_language: defaultLanguage,
            native_language: nativeLanguage
          });

        if (profileError) {
          // Handle duplicate username by appending random suffix
          if (profileError.message.includes('duplicate') && profileError.message.includes('username')) {
            const fallbackUsername = `${username}_${Math.random().toString(36).slice(2, 6)}`;
            await supabase
              .from('profiles')
              .insert({
                user_id: userId,
                username: fallbackUsername,
                default_language: defaultLanguage,
                native_language: nativeLanguage
              });
          } else {
            console.error('Profile creation error:', profileError);
          }
        }

      }

      // Ensure user_tier exists (in case trigger didn't fire)
      const { data: existingTier } = await supabase
        .from('user_tiers')
        .select('id')
        .eq('user_id', userId)
        .single();

      if (!existingTier) {
        await supabase
          .from('user_tiers')
          .insert({ user_id: userId, tier: 'free' });
      }
      // Welcome email is now triggered server-side via a database trigger on profile INSERT
    } catch (error) {
      console.error('Error ensuring profile exists:', error);
    }
  };

  // Check subscription status from Stripe
  // Uses refs to avoid dependency on user/session objects which change reference frequently
  const checkSubscription = useCallback(async (forceRefresh = false) => {
    // Prevent concurrent subscription checks
    if (subscriptionCheckInProgressRef.current) {
      console.log('[Auth] Skipping subscription check - already in progress');
      return;
    }
    
    const currentUser = userRef.current;
    const currentIsOffline = isOfflineRef.current;
    
    console.log('[Auth] checkSubscription called', { 
      hasUser: !!currentUser, 
      isOffline: currentIsOffline,
      forceRefresh
    });
    
    // Only check subscription if we have a user and we're online
    if (!currentUser || currentIsOffline) {
      console.log('[Auth] Skipping subscription check - conditions not met');
      return;
    }

    // DF-3: Return cached tier if valid and not forcing refresh
    if (!forceRefresh) {
      const cached = getCachedTier();
      if (cached) {
        console.log('[Auth] Using cached tier:', cached.tier);
        setTier(cached.tier as UserTier);
        setTierInitialized(true);
        setSubscriptionEnd(cached.subscriptionEnd);
        setPaymentStatus(cached.paymentStatus);
        setSubscriptionSource(cached.subscriptionSource);
        setCancelAtPeriodEnd(cached.cancelAtPeriodEnd ?? false);
        return;
      }
    } else {
      invalidateTierCache();
    }
    
    subscriptionCheckInProgressRef.current = true;
    setSubscriptionLoading(true);
    try {
      // Proactively refresh the session before making the API call
      // This prevents 401 errors from expired tokens
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !sessionData.session) {
        console.warn('[Auth] No valid session for subscription check, attempting refresh...');
        
        // Try to refresh the session
        const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
        
        if (refreshError || !refreshData.session) {
          console.warn('[Auth] Session refresh failed, defaulting to free tier');
          setTier('free');
          setTierInitialized(true);
          setSubscriptionEnd(null);
          setPaymentStatus(null);
          setSubscriptionSource(null);
          setCancelAtPeriodEnd(false);
          return;
        }
        
        console.log('[Auth] Session refreshed successfully');
      }
      
      console.log('[Auth] Invoking check-subscription function...');
      const { data, error } = await supabase.functions.invoke('check-subscription');
      
      console.log('[Auth] check-subscription response:', { data, error });
      
      // Handle auth errors gracefully - attempt refresh and retry once
      if (error) {
        console.error('[Auth] Subscription check error:', error);
        
        // 401 errors mean session is invalid - try refresh once
        if (error.message?.includes('401') || error.message?.includes('authorization')) {
          console.warn('[Auth] Auth invalid, attempting session refresh...');
          
          const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
          
          if (!refreshError && refreshData.session) {
            console.log('[Auth] Session refreshed, retrying subscription check...');
            // Retry the subscription check once
            const { data: retryData, error: retryError } = await supabase.functions.invoke('check-subscription');
            
            if (!retryError && retryData) {
              const newTier = retryData.tier as UserTier;
              console.log('[Auth] Retry successful, setting tier to:', newTier);
              setTier(newTier);
              setTierInitialized(true);
              setSubscriptionEnd(retryData.subscription_end || null);
              setPaymentStatus(retryData.payment_status || 'ok');
              setSubscriptionSource(retryData.subscription_source || null);
              setCancelAtPeriodEnd(!!retryData.cancel_at_period_end);
              // Cache the retry result
              setCachedTier({
                tier: newTier,
                subscriptionEnd: retryData.subscription_end || null,
                paymentStatus: retryData.payment_status || 'ok',
                subscriptionSource: retryData.subscription_source || null,
                cancelAtPeriodEnd: !!retryData.cancel_at_period_end,
              });
              
              if (currentUser.id) {
                storeUserTier(currentUser.id, newTier).catch(console.error);
              }
              return;
            }
          }
          
          console.warn('[Auth] Refresh failed, defaulting to free tier');
          setTier('free');
          setTierInitialized(true);
          setSubscriptionEnd(null);
          setPaymentStatus(null);
          setSubscriptionSource(null);
          setCancelAtPeriodEnd(false);
          return;
        }
        throw error;
      }
      
      if (data) {
        const newTier = data.tier as UserTier;
        console.log('[Auth] Setting tier to:', newTier);
        setTier(newTier);
        setTierInitialized(true);
        setSubscriptionEnd(data.subscription_end || null);
        setPaymentStatus(data.payment_status || 'ok');
        setSubscriptionSource(data.subscription_source || null);
        setCancelAtPeriodEnd(!!data.cancel_at_period_end);
        
        // DF-3: Cache in localStorage for TTL-based reuse
        setCachedTier({
          tier: newTier,
          subscriptionEnd: data.subscription_end || null,
          paymentStatus: data.payment_status || 'ok',
          subscriptionSource: data.subscription_source || null,
          cancelAtPeriodEnd: !!data.cancel_at_period_end,
        });
        
        // Cache tier for offline access (IndexedDB)
        if (currentUser.id) {
          storeUserTier(currentUser.id, newTier).catch(console.error);
        }
      } else {
        console.warn('[Auth] No data in subscription response');
      }
    } catch (error) {
      console.error('[Auth] Error checking subscription:', error);
      
      // CRITICAL: Always set tierInitialized on error to prevent infinite loading spinner.
      // Try to fall back to user_tiers table before defaulting to 'free'.
      try {
        const currentUser = userRef.current;
        if (currentUser) {
          const { data: tierData } = await supabase
            .from('user_tiers')
            .select('tier')
            .eq('user_id', currentUser.id)
            .maybeSingle();
          
          if (tierData?.tier) {
            console.log('[Auth] Fallback: loaded tier from database:', tierData.tier);
            setTier(tierData.tier as UserTier);
            setTierInitialized(true);
            return;
          }
        }
      } catch (fallbackError) {
        console.error('[Auth] Fallback tier lookup also failed:', fallbackError);
      }
      
      // Ultimate fallback: default to free so the app remains usable
      console.warn('[Auth] Defaulting to free tier after all checks failed');
      setTier('free');
      setTierInitialized(true);
    } finally {
      subscriptionCheckInProgressRef.current = false;
      setSubscriptionLoading(false);
    }
  }, []); // Empty deps - uses refs for current values

  // Set up periodic subscription refresh (every 60 seconds) - only when online
  // Use user.id to prevent re-triggering when user object reference changes
  useEffect(() => {
    if (user?.id && !isOffline) {
      // Only do initial check if we haven't checked for this user yet
      if (lastSubscriptionCheckUserIdRef.current !== user.id) {
        lastSubscriptionCheckUserIdRef.current = user.id;
        checkSubscription();
      }
      
      // Set up interval for periodic refresh
      refreshIntervalRef.current = setInterval(() => {
        checkSubscription();
      }, 60000); // 60 seconds
    }
    
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, [user?.id, isOffline]); // checkSubscription is stable (empty deps), so not needed here

  // Load cached tier when going offline or on initial offline load
  useEffect(() => {
    if (isOffline && user?.id) {
      getUserTier(user.id).then((cachedTier) => {
        if (cachedTier) {
          console.log('[Auth] Using cached tier for offline:', cachedTier);
          setTier(cachedTier as UserTier);
          setTierInitialized(true);
        }
      }).catch(console.error);
    }
  }, [isOffline, user?.id]);

  // (Welcome email logic is now inlined in ensureProfileExists to avoid closure issues)

  // Initialize auth - handle both online and offline scenarios
  useEffect(() => {
    if (initializingRef.current) return;
    initializingRef.current = true;

    // Set up auth state change listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('[Auth] State change:', event);

        // Intercept PASSWORD_RECOVERY before any other processing.
        // Without this, the recovery session looks like a normal SIGNED_IN,
        // ProtectedRoute sees an authenticated user, and the user gets
        // redirected to /translate without ever seeing the reset form.
        if (event === 'PASSWORD_RECOVERY') {
          console.log('[Auth] PASSWORD_RECOVERY detected, redirecting to reset page');
          setSession(session);
          setUser(session?.user ?? null);
          setLoading(false);
          if (window.location.pathname !== '/reset-password') {
            window.location.replace('/reset-password');
          }
          return;
        }

        // Graceful recovery on unexpected sign-out (e.g. stale token or server outage).
        // RFC-158: keep the cached user signed in while we try to recover; only
        // clear state when the error proves the session is truly gone.
        if (event === 'SIGNED_OUT') {
          const cached = getCachedSession();
          if (cached && cached.user?.id) {
            console.warn('[Auth] Unexpected SIGNED_OUT — attempting recovery from cache...');
            supabase.auth.refreshSession().then(({ data, error }) => {
              if (!error && data.session) {
                console.log('[Auth] Recovery succeeded — session restored');
                markHealthy();
              } else if (classifyAuthError(error) === 'transient') {
                console.warn('[Auth] Recovery hit a server problem — staying signed in', error?.message);
                markDegraded();
                setLoading(false);
              } else {
                console.warn('[Auth] Recovery failed — clearing state', error?.message);
                clearSignedOutState();
              }
            });
            return; // Keep current user/session until recovery resolves
          }

          clearSignedOutState();
          return;
        }

        setSession(session);
        setUser(session?.user ?? null);
        if (session) markHealthy();

        // Note: We don't set loading=false here - initializeSession handles that
        // to prevent cascading loading states that cause flicker

        // Only create profile after email is confirmed
        const isEmailConfirmed = session?.user?.email_confirmed_at != null;
        
        if (event === 'SIGNED_IN' && session?.user && isEmailConfirmed) {
          setTimeout(() => {
            ensureProfileExists(session.user.id, session.user.user_metadata);
          }, 0);
          
          // Store auth token for service worker access
          if (session.access_token) {
            storeAuthToken(session.user.id, session.access_token);
          }
          
          // Fetch tier and set loading states together to prevent cascading re-renders
          // This only runs for auth state changes AFTER initial load (e.g., tab focus restore)
          (async () => {
            // RFC 162: bounded, falls back to this user's device tier.
            const fetchedTier = ((await resolveInitialTier(session.user.id)) ?? 'free') as UserTier;

            // Set all states together to minimize re-renders
            setTier(fetchedTier);
            setTierInitialized(true);
            setLoading(false);
          })();
          
          // Pre-cache vocabulary immediately after login
          syncVocabularyFromServer(session.user.id).then((success) => {
            if (success) {
              console.log('[Auth] Vocabulary pre-cached on login');
            }
          });
        }
        
        // Update token in storage when refreshed
        if (event === 'TOKEN_REFRESHED' && session?.access_token && session?.user) {
          storeAuthToken(session.user.id, session.access_token);
        }
      }
    );

    // Get initial session with offline fallback
    const initializeSession = async () => {
      const currentlyOffline = !navigator.onLine;
      
      if (currentlyOffline) {
        // Immediately use cached session when offline
        console.log('[Auth] Offline - using cached session');
        const cached = getCachedSession();
        if (cached) {
          setUser(cached.user);
          setSession(cached.session);
          
          // Load cached tier
          if (cached.user.id) {
            const cachedTier = await getUserTier(cached.user.id);
            if (cachedTier) {
              setTier(cachedTier as UserTier);
              setTierInitialized(true);
            }
          }
        }
        setLoading(false);
        setTierInitialized(true); // Mark as initialized even if no user (not logged in)
        return;
      }

      // Online: race between getSession and timeout
      try {
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => reject(new Error('Session fetch timeout')), SESSION_TIMEOUT_MS);
        });

        const sessionPromise = supabase.auth.getSession();
        
        const result = await Promise.race([sessionPromise, timeoutPromise]);
        const { data: { session } } = result;
        
        setSession(session);
        setUser(session?.user ?? null);

        // Only create profile if email is confirmed
        const isEmailConfirmed = session?.user?.email_confirmed_at != null;
        if (session?.user && isEmailConfirmed) {
          ensureProfileExists(session.user.id, session.user.user_metadata);
          
          // RFC 162: bounded tier read so a weak connection cannot hold
          // every page behind it; falls back to this user's device tier.
          const initialTier = await resolveInitialTier(session.user.id);
          if (initialTier) setTier(initialTier as UserTier);
          setTierInitialized(true);
        } else {
          // No user or email not confirmed - mark as initialized with default tier
          setTierInitialized(true);
        }
      } catch (error) {
        // Timeout or network error - fall back to cached session
        console.warn('[Auth] Session fetch failed, using cached session:', error);
        const cached = getCachedSession();
        if (cached) {
          setUser(cached.user);
          setSession(cached.session);
          
          // Load cached tier
          if (cached.user.id) {
            const cachedTier = await getUserTier(cached.user.id);
            if (cachedTier) {
              setTier(cachedTier as UserTier);
            }
          }
        }
        setTierInitialized(true);
      } finally {
        setLoading(false);
      }
    };

    initializeSession();

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, username: string, defaultLanguage?: string, intent?: string, nativeLanguage?: string) => {
    try {
      // Always use production URL for email redirects (important for native app deep linking)
      const PRODUCTION_BASE_URL = 'https://lekalearning.com';
      let redirectUrl = `${PRODUCTION_BASE_URL}/translate?confirmed=true`;
      if (intent) {
        redirectUrl += `&intent=${intent}`;
      }
      
      // Route signup through edge function for server-side password validation
      const { data, error: fnError } = await supabase.functions.invoke('signup', {
        body: {
          email,
          password,
          username,
          default_language: defaultLanguage || 'spanish',
          native_language: nativeLanguage || 'english',
          redirect_url: redirectUrl,
        },
      });

      if (fnError) {
        // Parse the actual error message from the edge function response body
        try {
          const errorBody = await fnError.context?.json?.();
          if (errorBody?.error) {
            throw new Error(errorBody.error);
          }
        } catch (parseErr) {
          // If parseErr is our extracted error, rethrow it
          if (parseErr instanceof Error && parseErr.message !== fnError.message) {
            throw parseErr;
          }
        }
        throw fnError;
      }
      
      // Check for application-level errors returned in the response body
      if (data?.error) {
        throw new Error(data.error);
      }

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signOut = async () => {
    clearPrivateQueries(queryClient);
    // Clear cached session FIRST so the SIGNED_OUT handler doesn't try recovery
    try { localStorage.removeItem(AUTH_STORAGE_KEY); } catch { /* ignore */ }
    invalidateTierCache(); // DF-3: prevent tier leaking to next user
    const currentUserId = user?.id;
    
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error('Sign out error:', error);
    }
    
    // Clear offline cache and auth token for the user
    if (currentUserId) {
      try {
        await clearUserData(currentUserId);
        await clearAuthToken(currentUserId);
      } catch (error) {
        console.error('Error clearing offline cache:', error);
      }
    }
    
    // Always clear local state
    setUser(null);
    setSession(null);
    setTier('free');
    setSubscriptionEnd(null);
    setPaymentStatus(null);
    setSubscriptionSource(null);
    setCancelAtPeriodEnd(false);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      session, 
      loading,
      isOffline,
      tier,
      tierInitialized,
      subscriptionEnd, 
      subscriptionLoading,
      paymentStatus,
      subscriptionSource,
      cancelAtPeriodEnd,
      checkSubscription,
      signUp, 
      signIn, 
      signOut,
      connectionState,
      retryConnection,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
